import { Check, Circle, CircleDashed, Minus, TriangleAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';

import { destinationOf } from '../../hooks/use-destination-copy';
import type { ProductTimelineEntry } from '../../types/meta-delivery.types';
import { deriveLifecycle, type LifecycleState } from '../../utils/delivery-lifecycle';

const STATE_STYLE: Record<LifecycleState, { icon: typeof Check; node: string; text: string }> = {
  done: { icon: Check, node: 'border-success bg-success-50 text-success', text: 'text-foreground' },
  failed: { icon: X, node: 'border-error bg-error-50 text-error', text: 'text-foreground' },
  warning: {
    icon: TriangleAlert,
    node: 'border-warning bg-warning-50 text-warning',
    text: 'text-foreground',
  },
  pending: {
    icon: CircleDashed,
    node: 'border-info bg-info-50 text-info',
    text: 'text-foreground',
  },
  skipped: {
    icon: Minus,
    node: 'border-muted-foreground/30 bg-muted text-muted-foreground',
    text: 'text-muted-foreground',
  },
};

/**
 * The product's path through one delivery job as four stages — prepared → sent
 * → held by the destination → approval — each with its state and, only where
 * the audit records one, a time. The audit row is overwritten in place, so the
 * strip never invents the intermediate times it doesn't have.
 */
export function DeliveryLifecycleStrip({ entry }: { entry: ProductTimelineEntry }) {
  const { t } = useTranslation('deliveries');
  const destination = destinationOf(entry.connectorCode);
  const steps = deriveLifecycle(entry);

  return (
    <ol
      data-testid={`delivery-lifecycle-strip-${entry.deliveryJobId}`}
      className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 rounded-lg border p-3 sm:grid-cols-4"
      aria-label={t('productLog.lifecycle.ariaLabel')}
    >
      {steps.map((step, i) => {
        const style = STATE_STYLE[step.state];
        const Icon = step.state === 'pending' && i === 0 ? Circle : style.icon;
        return (
          <li
            key={step.stage}
            data-testid={`delivery-lifecycle-strip-${entry.deliveryJobId}-step-${step.stage}`}
            className="flex min-w-0 items-start gap-2"
          >
            <span
              aria-hidden="true"
              className={cn(
                'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border',
                style.node,
              )}
            >
              <Icon className="size-3" />
            </span>
            <div className="min-w-0">
              <p className={cn('text-xs font-semibold', style.text)}>
                {t(`productLog.lifecycle.stage.${step.stage}`, {
                  destination: t(`productLog.destination.${destination}`),
                })}
              </p>
              <p className="text-muted-foreground text-xs">
                {t(`productLog.lifecycle.detail.${step.detail}`, {
                  destination: t(`productLog.destination.${destination}`),
                })}
              </p>
              {step.at ? (
                <p className="text-muted-foreground text-[11px] tabular-nums">
                  {formatDateTime(step.at)}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
